import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Flower2, ImageIcon, MailPlus, Heart } from "lucide-react";
import { roseBackdrop, ROSE_COLOR_HEX } from "@/lib/roses";
import { fileUrl } from "@/lib/api";
import { RoseGiftDialog } from "@/components/RoseGiftDialog";
import { HeartButton } from "@/components/HeartButton";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export function NoteCard({ note, index = 0, onGift }) {
  const [giftOpen, setGiftOpen] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const backdrop = roseBackdrop(note.roses);
  const date = note.created_at ? new Date(note.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";

  return (
    <>
      <article
        data-testid={`note-card-${note.id}`}
        className="relative overflow-hidden rounded-2xl border border-pink-500/25 p-6 flex flex-col justify-between min-h-[280px] transition-all duration-300 hover:border-pink-400 hover:shadow-[0_0_30px_rgba(255,46,147,0.25)] hover:-translate-y-1 float-up"
        style={{ animationDelay: `${Math.min(index * 60, 600)}ms` }}
      >
        {backdrop ? (
          <>
            <img src={backdrop} alt="" className="absolute inset-0 w-full h-full object-cover brightness-[0.6] contrast-110 saturate-125" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/25 backdrop-blur-[0.5px]" />
          </>
        ) : (
          <div className="absolute inset-0 card-glass" />
        )}

        <div className="relative z-10 flex flex-col justify-between h-full">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-pink-400">For</p>
              <Link
                to={note.recipient_id ? `/member/${note.recipient_id}` : "#"}
                className="font-serif text-2xl font-semibold text-pink-100 hover:text-pink-300 transition-colors"
                data-testid={`note-recipient-${note.id}`}
              >
                {note.recipient_name}
              </Link>
            </div>
            {note.roses?.length > 0 && (
              <div className="flex items-center gap-1 bg-black/50 rounded-full px-2.5 py-1 border border-pink-500/30">
                {note.roses.map((c, i) => (
                  <Flower2 key={i} className="w-3.5 h-3.5" style={{ color: ROSE_COLOR_HEX[c] }} />
                ))}
              </div>
            )}
          </div>

          <p className="font-serif text-xl md:text-2xl leading-relaxed text-pink-50 font-light italic my-4 flex-1">
            "{note.message}"
          </p>

          {note.image && (
            <button
              onClick={() => setLightbox(true)}
              data-testid={`note-image-${note.id}`}
              className="mb-3 self-start"
            >
              <img src={fileUrl(note.image)} alt="attached" className="h-20 w-20 object-cover rounded-lg border border-pink-500/30 hover:scale-105 transition-transform" />
            </button>
          )}

          <div className="flex items-center justify-between pt-3 border-t border-pink-500/15">
            <div className="text-sm">
              <span className="text-slate-400">— </span>
              <span className="text-pink-200 font-medium">{note.author_name || "Anonymous"}</span>
              <span className="text-slate-500 text-xs ml-2">{date}</span>
            </div>
            <div className="flex items-center gap-2">
            <HeartButton note={note} />
            <button
              data-testid={`note-gift-btn-${note.id}`}
              onClick={() => setGiftOpen(true)}
              className="flex items-center gap-1.5 text-xs bg-pink-500/15 hover:bg-pink-500/30 text-pink-200 px-3 py-1.5 rounded-full border border-pink-500/25 transition-all"
              title="Gift roses"
            >
              <Flower2 className="w-3.5 h-3.5" /> Roses
            </button>
            </div>
          </div>
        </div>
      </article>

      <RoseGiftDialog open={giftOpen} onOpenChange={setGiftOpen} note={note} />

      <Dialog open={lightbox} onOpenChange={setLightbox}>
        <DialogContent className="bg-black/95 border-pink-500/25 max-w-2xl p-2">
          {note.image && <img src={fileUrl(note.image)} alt="attached" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>
    </>
  );
}
